import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    AddJdOpsProductT,
    ApplicationActivityT,
    PlantingActivityT,
    SeedCostT,
    TillageWorkT,
} from "../types/addCost";
import {
    addApplicationActivity as addApplicationActivityApi,
    addJdOpsProduct as addJdOpsProductApi,
    addPlantingActivity as addPlantingActivityApi,
    addSeedCost as addSeedCostApi,
    addTillageWork as addTillageWorkApi,
} from "../services/operationcostapi";

type AddOperationKey =
    | "jdOpsProduct"
    | "seedCost"
    | "applicationActivity"
    | "plantingActivity"
    | "tillageWork";

type AddOperationState = {
    loading: boolean;
    success: boolean;
    error: string | null;
};

export interface AddCostState {
    operations: Record<AddOperationKey, AddOperationState>;
}

const defaultOperationState: AddOperationState = {
    loading: false,
    success: false,
    error: null,
};

const initialState: AddCostState = {
    operations: {
        jdOpsProduct: { ...defaultOperationState },
        seedCost: { ...defaultOperationState },
        applicationActivity: { ...defaultOperationState },
        plantingActivity: { ...defaultOperationState },
        tillageWork: { ...defaultOperationState },
    },
};

export const addJdOpsProduct = createAsyncThunk(
    "JdOpsProduct/jdopsproducts/pricing",
    async (data: AddJdOpsProductT) => {
        const response = await addJdOpsProductApi(data);
        return response.data;
    }
);

export const addSeedCost = createAsyncThunk(
    "SeedCost/seedcosts",
    async (data: SeedCostT) => {
        const response = await addSeedCostApi(data);
        return response.data;
    }
);

export const addApplicationActivity = createAsyncThunk(
    "ApplicationActivity/applicationactivity/create",
    async (data: ApplicationActivityT) => {
        const response = await addApplicationActivityApi(data);
        return response.data;
    }
);

export const addPlantingActivity = createAsyncThunk(
    "PlantingActivity/plantingactivity/create",
    async (data: PlantingActivityT) => {
        const response = await addPlantingActivityApi(data);
        return response.data;
    }
);

export const addTillageWork = createAsyncThunk(
    "TillageWork/Create",
    async (data: TillageWorkT) => {
        const response = await addTillageWorkApi(data);
        return response.data;
    }
);

const addCostSlice = createSlice({
    name: "addCost",
    initialState,
    reducers: {
        resetAddOperationState: (state, action: { payload: AddOperationKey }) => {
            state.operations[action.payload] = { ...defaultOperationState };
        },
        resetAllAddOperationStates: (state) => {
            state.operations = {
                jdOpsProduct: { ...defaultOperationState },
                seedCost: { ...defaultOperationState },
                applicationActivity: { ...defaultOperationState },
                plantingActivity: { ...defaultOperationState },
                tillageWork: { ...defaultOperationState },
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(addJdOpsProduct.pending, (state) => {
                state.operations.jdOpsProduct.loading = true;
                state.operations.jdOpsProduct.success = false;
                state.operations.jdOpsProduct.error = null;
            })
            .addCase(addJdOpsProduct.fulfilled, (state) => {
                state.operations.jdOpsProduct.loading = false;
                state.operations.jdOpsProduct.success = true;
                state.operations.jdOpsProduct.error = null;
            })
            .addCase(addJdOpsProduct.rejected, (state, action) => {
                state.operations.jdOpsProduct.loading = false;
                state.operations.jdOpsProduct.success = false;
                state.operations.jdOpsProduct.error = action.error.message || "Failed to add JD Ops Product";
            })
            .addCase(addSeedCost.pending, (state) => {
                state.operations.seedCost.loading = true;
                state.operations.seedCost.success = false;
                state.operations.seedCost.error = null;
            })
            .addCase(addSeedCost.fulfilled, (state) => {
                state.operations.seedCost.loading = false;
                state.operations.seedCost.success = true;
                state.operations.seedCost.error = null;
            })
            .addCase(addSeedCost.rejected, (state, action) => {
                state.operations.seedCost.loading = false;
                state.operations.seedCost.success = false;
                state.operations.seedCost.error = action.error.message || "Failed to add Seed Cost";
            })
            .addCase(addApplicationActivity.pending, (state) => {
                state.operations.applicationActivity.loading = true;
                state.operations.applicationActivity.success = false;
                state.operations.applicationActivity.error = null;
            })
            .addCase(addApplicationActivity.fulfilled, (state) => {
                state.operations.applicationActivity.loading = false;
                state.operations.applicationActivity.success = true;
                state.operations.applicationActivity.error = null;
            })
            .addCase(addApplicationActivity.rejected, (state, action) => {
                state.operations.applicationActivity.loading = false;
                state.operations.applicationActivity.success = false;
                state.operations.applicationActivity.error = action.error.message || "Failed to add Application Activity";
            })
            .addCase(addPlantingActivity.pending, (state) => {
                state.operations.plantingActivity.loading = true;
                state.operations.plantingActivity.success = false;
                state.operations.plantingActivity.error = null;
            })
            .addCase(addPlantingActivity.fulfilled, (state) => {
                state.operations.plantingActivity.loading = false;
                state.operations.plantingActivity.success = true;
                state.operations.plantingActivity.error = null;
            })
            .addCase(addPlantingActivity.rejected, (state, action) => {
                state.operations.plantingActivity.loading = false;
                state.operations.plantingActivity.success = false;
                state.operations.plantingActivity.error = action.error.message || "Failed to add Planting Activity";
            })
            .addCase(addTillageWork.pending, (state) => {
                state.operations.tillageWork.loading = true;
                state.operations.tillageWork.success = false;
                state.operations.tillageWork.error = null;
            })
            .addCase(addTillageWork.fulfilled, (state) => {
                state.operations.tillageWork.loading = false;
                state.operations.tillageWork.success = true;
                state.operations.tillageWork.error = null;
            })
            .addCase(addTillageWork.rejected, (state, action) => {
                state.operations.tillageWork.loading = false;
                state.operations.tillageWork.success = false;
                state.operations.tillageWork.error = action.error.message || "Failed to add Tillage Work";
            });
    },
});

export const { resetAddOperationState, resetAllAddOperationStates } = addCostSlice.actions;
export default addCostSlice.reducer;